/**
 * Global Job Notification Banner
 *
 * Displays persistent notifications for:
 * - Active jobs (generating, queued)
 * - Completed jobs (success)
 * - Failed jobs (with error details and retry option)
 *
 * This banner persists across page navigation to ensure users always know
 * the status of their session generation.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Loader2,
  CheckCircle,
  XCircle,
  X,
  Play,
  RotateCcw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useJobTracker, TrackedJob, JobType } from '@/contexts/JobTrackerContext';
import { cn } from '@/lib/utils';

const JOB_TYPE_LABELS: Record<JobType, string> = {
  meditation: 'Meditation',
  zen: 'Zen Session',
  affirmation: 'Affirmation',
  sleep: 'Sleep Session',
  unknown: 'Session',
};

const JobProgressItem: React.FC<{
  job: TrackedJob;
  onAcknowledge: () => void;
}> = ({ job, onAcknowledge }) => {
  const navigate = useNavigate();
  const label = JOB_TYPE_LABELS[job.type];

  const getStatusMessage = () => {
    switch (job.status) {
      case 'queued':
        return job.queuePosition
          ? `Position #${job.queuePosition} in queue`
          : 'Waiting in queue...';
      case 'processing':
        return `Generating... ${Math.round(job.progress * 100)}%`;
      case 'completed':
        return 'Ready to play!';
      case 'failed':
        return job.errorMessage || 'Generation failed';
      default:
        return 'Processing...';
    }
  };

  const handlePlaySession = () => {
    if (job.resultData?.sessionId) {
      navigate('/sessions/history');
    }
    onAcknowledge();
  };

  const handleRetry = () => {
    // Navigate back to the category page to retry
    navigate('/app');
    onAcknowledge();
  };

  return (
    <div className="flex items-center gap-3 py-2">
      {/* Status Icon */}
      <div className="flex-shrink-0">
        {job.status === 'completed' ? (
          <CheckCircle className="h-5 w-5 text-green-500" />
        ) : job.status === 'failed' ? (
          <XCircle className="h-5 w-5 text-red-500" />
        ) : (
          <Loader2 className="h-5 w-5 text-primary animate-spin" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{label}</span>
          <span className="text-xs text-muted-foreground">
            {getStatusMessage()}
          </span>
        </div>

        {/* Progress bar for active jobs */}
        {['queued', 'processing'].includes(job.status) && (
          <Progress
            value={job.progress * 100}
            className="h-1.5 mt-1"
          />
        )}

        {/* Error details for failed jobs */}
        {job.status === 'failed' && job.errorMessage && (
          <p className="text-xs text-red-600 mt-1 line-clamp-2">
            {job.errorMessage}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {job.status === 'completed' && (
          <Button
            size="sm"
            variant="default"
            onClick={handlePlaySession}
            className="h-7 px-2 text-xs"
          >
            <Play className="h-3 w-3 mr-1" />
            Play
          </Button>
        )}

        {job.status === 'failed' && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleRetry}
            className="h-7 px-2 text-xs"
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Retry
          </Button>
        )}

        {/* Dismiss button for completed/failed */}
        {['completed', 'failed'].includes(job.status) && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onAcknowledge}
            className="h-7 w-7 p-0"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export const GlobalJobNotificationBanner: React.FC = () => {
  const {
    activeJobs,
    recentlyCompleted,
    recentlyFailed,
    acknowledgeJob,
    hasActiveJobs,
    hasUnacknowledgedCompletions,
    hasUnacknowledgedFailures,
  } = useJobTracker();

  const [expanded, setExpanded] = React.useState(true);

  // Filter to only show unacknowledged completed/failed jobs
  const unacknowledgedCompleted = recentlyCompleted.filter(j => !j.acknowledged);
  const unacknowledgedFailed = recentlyFailed.filter(j => !j.acknowledged);

  // Nothing to show
  if (!hasActiveJobs && !hasUnacknowledgedCompletions && !hasUnacknowledgedFailures) {
    return null;
  }

  const totalJobs = activeJobs.length + unacknowledgedCompleted.length + unacknowledgedFailed.length;

  // Determine banner variant based on status
  const hasFailed = unacknowledgedFailed.length > 0;
  const hasCompleted = unacknowledgedCompleted.length > 0;
  const variant = hasFailed ? 'destructive' : hasCompleted ? 'default' : 'default';

  // Determine banner icon and title
  const getHeaderInfo = () => {
    if (hasFailed && !hasActiveJobs && !hasCompleted) {
      return {
        icon: <XCircle className="h-5 w-5" />,
        title: `${unacknowledgedFailed.length} session${unacknowledgedFailed.length > 1 ? 's' : ''} failed`,
        className: 'border-red-500 bg-red-50 dark:bg-red-950/20',
      };
    }
    if (hasCompleted && !hasActiveJobs && !hasFailed) {
      return {
        icon: <CheckCircle className="h-5 w-5 text-green-500" />,
        title: `${unacknowledgedCompleted.length} session${unacknowledgedCompleted.length > 1 ? 's' : ''} ready`,
        className: 'border-green-500 bg-green-50 dark:bg-green-950/20',
      };
    }
    if (hasActiveJobs) {
      return {
        icon: <Loader2 className="h-5 w-5 animate-spin" />,
        title: `${activeJobs.length} session${activeJobs.length > 1 ? 's' : ''} generating...`,
        className: 'border-primary bg-primary/5',
      };
    }
    return {
      icon: <Loader2 className="h-5 w-5" />,
      title: 'Session Updates',
      className: 'border-primary bg-primary/5',
    };
  };

  const headerInfo = getHeaderInfo();

  return (
    <Alert
      className={cn(
        'mb-4 border-l-4 shadow-md',
        headerInfo.className
      )}
    >
      {headerInfo.icon}
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <AlertTitle className="font-bold text-base mb-0">
            {headerInfo.title}
          </AlertTitle>
          <div className="flex items-center gap-2">
            {totalJobs > 1 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded(!expanded)}
                className="h-6 px-2 text-xs"
              >
                {expanded ? (
                  <>
                    <ChevronUp className="h-3 w-3 mr-1" />
                    Hide
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3 w-3 mr-1" />
                    Show ({totalJobs})
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {expanded && (
          <AlertDescription className="mt-2">
            <div className="space-y-1 divide-y divide-border/50">
              {/* Active jobs first */}
              {activeJobs.map(job => (
                <JobProgressItem
                  key={job.id}
                  job={job}
                  onAcknowledge={() => acknowledgeJob(job.id)}
                />
              ))}

              {/* Then failed jobs (important) */}
              {unacknowledgedFailed.map(job => (
                <JobProgressItem
                  key={job.id}
                  job={job}
                  onAcknowledge={() => acknowledgeJob(job.id)}
                />
              ))}

              {/* Then completed jobs */}
              {unacknowledgedCompleted.map(job => (
                <JobProgressItem
                  key={job.id}
                  job={job}
                  onAcknowledge={() => acknowledgeJob(job.id)}
                />
              ))}
            </div>
          </AlertDescription>
        )}
      </div>
    </Alert>
  );
};
